/**
 * `src/lib/pwa/install.ts` — ask people to install the app when they open it
 * in a browser.
 *
 * Owner request: whenever the app is opened from the browser rather than as
 * the installed app, ask to install it. Browsers differ, so there are three
 * offers and a deliberate "nothing":
 *
 *   'native'  Chromium (Android Chrome, Samsung Internet, desktop Chrome/Edge)
 *             fires `beforeinstallprompt` while the app is installable and not
 *             installed. We keep that event and show our own button, which
 *             opens the browser's real install dialog.
 *   'ios'     Safari on iPhone/iPad has no install API at all. When the page is
 *             not running from the home screen, show the three steps instead.
 *   'manual'  Android, when no `beforeinstallprompt` came. Chrome withholds the
 *             event for a while after the app is uninstalled (and Firefox never
 *             sends it), which used to mean the app silently stopped asking.
 *             After a short wait, show where the browser's own menu item is.
 *   'none'    Running as the installed app, known to be installed, a desktop
 *             browser that offers nothing, or already dismissed in this visit.
 *
 * "Installed" is asked of the browser where it can answer:
 * `navigator.getInstalledRelatedApps()` lists this app when the manifest's
 * `related_applications` names it (vite.config.ts) and it is installed. That
 * is what keeps 'manual' from nagging someone who has the app and just opened
 * a link in Chrome.
 *
 * "Each time he opens it" is one ask per visit: dismissing it hides it until
 * the browser tab is opened again (sessionStorage), not on every page change.
 */

export type InstallOffer = 'none' | 'native' | 'ios' | 'manual';

export interface OfferInput {
	/** Running as the installed app (display-mode standalone, iOS `standalone`). */
	standalone: boolean;
	/** The browser says the app is installed (`getInstalledRelatedApps`). */
	installed: boolean;
	/** iPhone, iPod or iPad (including iPadOS reporting as a Mac). */
	ios: boolean;
	/** An Android phone or tablet. */
	android: boolean;
	/** A `beforeinstallprompt` event is in hand. */
	canPrompt: boolean;
	/** Long enough has passed that `beforeinstallprompt` is not coming. */
	waited: boolean;
	/** Dismissed earlier in this visit. */
	dismissed: boolean;
}

/** Pure: what to offer, given what the platform says. */
export function offerFor(input: OfferInput): InstallOffer {
	if (input.standalone || input.installed || input.dismissed) return 'none';
	if (input.canPrompt) return 'native';
	if (input.ios) return 'ios';
	if (input.android && input.waited) return 'manual';
	return 'none';
}

interface InstallPromptEvent extends Event {
	prompt(): Promise<void>;
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type RelatedAppsNavigator = Navigator & {
	getInstalledRelatedApps?: () => Promise<readonly unknown[]>;
};

const DISMISSED_KEY = 'ghuraba.install.dismissed';

/** How long to wait for `beforeinstallprompt` before showing the menu steps.
 *  Chrome fires it within a second or two of load when it is going to. */
export const PROMPT_WAIT_MS = 3500;

let deferred: InstallPromptEvent | null = null;
let installed = false;
let waited = false;
let started = false;
const listeners = new Set<(offer: InstallOffer) => void>();

export function isStandalone(): boolean {
	if (typeof window === 'undefined') return false;
	const nav = navigator as Navigator & { standalone?: boolean };
	return (
		nav.standalone === true ||
		window.matchMedia?.('(display-mode: standalone)').matches === true ||
		window.matchMedia?.('(display-mode: fullscreen)').matches === true ||
		document.referrer.startsWith('android-app://')
	);
}

export function isIos(): boolean {
	if (typeof navigator === 'undefined') return false;
	return (
		/iphone|ipad|ipod/i.test(navigator.userAgent) ||
		(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
	);
}

export function isAndroid(): boolean {
	if (typeof navigator === 'undefined') return false;
	return /android/i.test(navigator.userAgent);
}

function wasDismissed(): boolean {
	try {
		return sessionStorage.getItem(DISMISSED_KEY) === '1';
	} catch {
		return false;
	}
}

export function currentOffer(): InstallOffer {
	return offerFor({
		standalone: isStandalone(),
		installed,
		ios: isIos(),
		android: isAndroid(),
		canPrompt: deferred !== null,
		waited,
		dismissed: wasDismissed()
	});
}

function emit(): void {
	const offer = currentOffer();
	for (const listener of listeners) listener(offer);
}

/**
 * Start listening. Must run as early as possible: `beforeinstallprompt` can
 * fire before the page's components mount, and a missed event is not resent.
 */
export function startInstallWatch(): void {
	if (started || typeof window === 'undefined') return;
	started = true;
	window.addEventListener('beforeinstallprompt', (event) => {
		// Stop the browser's own mini-infobar; our prompt replaces it.
		event.preventDefault();
		deferred = event as InstallPromptEvent;
		installed = false;
		emit();
	});
	window.addEventListener('appinstalled', () => {
		installed = true;
		deferred = null;
		emit();
	});

	const nav = navigator as RelatedAppsNavigator;
	if (typeof nav.getInstalledRelatedApps === 'function') {
		void nav
			.getInstalledRelatedApps()
			.then((apps) => {
				if (apps.length > 0) {
					installed = true;
					emit();
				}
			})
			.catch(() => {
				// Not answerable here; the other signals still decide.
			});
	}

	setTimeout(() => {
		waited = true;
		emit();
	}, PROMPT_WAIT_MS);
}

export function onInstallOffer(listener: (offer: InstallOffer) => void): () => void {
	listeners.add(listener);
	listener(currentOffer());
	return () => listeners.delete(listener);
}

/** Open the browser's install dialog. `true` when the person installed. */
export async function promptInstall(): Promise<boolean> {
	const event = deferred;
	if (!event) return false;
	deferred = null; // a prompt event can be used once
	await event.prompt();
	const { outcome } = await event.userChoice;
	emit();
	return outcome === 'accepted';
}

/** "Not now": hide until the app is opened again in a new visit. */
export function dismissInstall(): void {
	try {
		sessionStorage.setItem(DISMISSED_KEY, '1');
	} catch {
		// Storage blocked: it simply asks again next time.
	}
	emit();
}
